import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  AccreditationEligibilityCriteriaEnumType,
  AccreditationStatusEnumType,
} from '__generated__/graphql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import Alert from 'primitives/Alert';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

const INVESTMENT_ENTITY_QUERY = gql(`
  query getInvestmentEntityInvestorApplication($id: ID!) {
    investmentEntity(id: $id) {
      id
      investorApplication {
        id
        accreditationStatus
        accreditationEligibilityCriteria
        accreditationProofDocument {
          url
          name
        }
        targetAllocation
      }
      investorReminders {
        type
        sentAt
        emailStatus
      }
    }
  }
`);

const UPSERT_ACCREDITATION_APPLICATION_MUTATION = gql(`
  mutation UpsertAccreditationApplication(
    $investmentEntityId: ID!
    $accreditationStatus: AccreditationStatusEnumType!
    $accreditationProofDocument: Upload
    $accreditationEligibilityCriteria: [AccreditationEligibilityCriteriaEnumType!]
  ) {
    upsertAccreditationApplication(
      investmentEntityId: $investmentEntityId
      accreditationStatus: $accreditationStatus
      accreditationProofDocument: $accreditationProofDocument
      accreditationEligibilityCriteria: $accreditationEligibilityCriteria
    ) {
      id
      accreditationStatus
      accreditationProofDocument {
        url
        name
      }
      accreditationEligibilityCriteria
    }
  }
`);

const InvestmentEntityInvestorApplication: FC = () => {
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITY_QUERY, {
    variables: {
      id: investmentEntityId,
    },
  });

  const [upsertAccreditationApplication, { loading: mutationLoading, error: mutationError }] =
    useMutation(UPSERT_ACCREDITATION_APPLICATION_MUTATION, {
      refetchQueries: ['getInvestmentEntityInvestorApplication'],
    });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntity = data.investmentEntity;

  if (!investmentEntity.investorApplication) {
    return (
      <>
        <div className="flex flex-col gap-4 mt-4 border border-gray-200 rounded-md p-4">
          <Alert
            type="info"
            title="Accreditation Details are needed"
            message="Please enter the accreditation details for the investor application."
          />
          <FormPanel
            loading={loading || mutationLoading}
            error={error || mutationError}
            onSubmit={async formData => {
              try {
                await upsertAccreditationApplication({
                  variables: {
                    investmentEntityId,
                    accreditationStatus: formData.accreditationStatus,
                    accreditationProofDocument:
                      investmentEntity.investorApplication?.accreditationProofDocument !==
                      formData.accreditationProofDocument
                        ? formData.accreditationProofDocument
                        : undefined,
                    accreditationEligibilityCriteria:
                      formData.accreditationEligibilityCriteria || [],
                  },
                });
              } catch (err) {}
            }}
            submitButtonLabel="Submit"
          >
            <FormInput
              fullWidth
              type="radio-group"
              fieldName="accreditationStatus"
              label="Accreditation Status"
              defaultValue={undefined}
              options={[
                {
                  value: AccreditationStatusEnumType.PendingInvestorResponse,
                  label: 'Pending Investor Response',
                  color: 'bg-gray-400',
                },
                {
                  value: AccreditationStatusEnumType.InitiateProcess,
                  label: 'Initiate Process',
                  color: 'bg-amber-300',
                },
                {
                  value: AccreditationStatusEnumType.PendingAlindiaVerification,
                  label: 'Pending AL India Verification',
                  color: 'bg-orange-400',
                },
                {
                  value: AccreditationStatusEnumType.Accredited,
                  label: 'Accredited',
                  color: 'bg-green-400',
                },
                {
                  value: AccreditationStatusEnumType.NotAccredited,
                  label: 'Not Accredited',
                  color: 'bg-red-500',
                },
              ]}
            />
            <FormInput
              fieldName="accreditationProofDocument"
              label="Accreditation Proof Document *"
              helperText="Accredited Investor (AI) Certificate"
              type="upload"
              defaultValue={undefined}
            />
            <FormInput
              fullWidth
              type="checkbox-group"
              fieldName="accreditationEligibilityCriteria"
              label="Which of the following criteria do they qualify for?"
              defaultValue={undefined}
              compact={true}
              options={[
                {
                  value: AccreditationEligibilityCriteriaEnumType.AnnualIncome,
                  label: 'Annual Income',
                },
                {
                  value: AccreditationEligibilityCriteriaEnumType.NetWorth,
                  label: 'Net Worth',
                },
                {
                  value: AccreditationEligibilityCriteriaEnumType.Combined,
                  label: 'Combined',
                },
              ]}
            />
          </FormPanel>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      <FormPanelWithReadMode
        loading={loading || mutationLoading}
        error={error || mutationError}
        onSubmit={async formData => {
          try {
            await upsertAccreditationApplication({
              variables: {
                investmentEntityId,
                accreditationStatus: formData.accreditationStatus,
                accreditationProofDocument:
                  investmentEntity.investorApplication?.accreditationProofDocument !==
                  formData.accreditationProofDocument
                    ? formData.accreditationProofDocument
                    : undefined,
                accreditationEligibilityCriteria: formData.accreditationEligibilityCriteria || [],
              },
            });
          } catch (err) {}
        }}
        submitButtonLabel="Submit"
      >
        <FormInput
          fullWidth
          type="radio-group"
          fieldName="accreditationStatus"
          label="Accreditation Status"
          defaultValue={investmentEntity.investorApplication?.accreditationStatus}
          options={[
            {
              value: AccreditationStatusEnumType.PendingInvestorResponse,
              label: 'Pending Investor Response',
              color: 'bg-gray-400',
            },
            {
              value: AccreditationStatusEnumType.InitiateProcess,
              label: 'Initiate Process',
              color: 'bg-amber-300',
            },
            {
              value: AccreditationStatusEnumType.PendingAlindiaVerification,
              label: 'Pending AL India Verification',
              color: 'bg-orange-400',
            },
            {
              value: AccreditationStatusEnumType.Accredited,
              label: 'Accredited',
              color: 'bg-green-400',
            },
            {
              value: AccreditationStatusEnumType.NotAccredited,
              label: 'Not Accredited',
              color: 'bg-red-500',
            },
          ]}
        />
        <FormInput
          fieldName="accreditationProofDocument"
          label="Accreditation Proof Document *"
          helperText="Accredited Investor (AI) Certificate"
          type="upload"
          defaultValue={investmentEntity.investorApplication?.accreditationProofDocument}
        />
        <FormInput
          fullWidth
          type="checkbox-group"
          fieldName="accreditationEligibilityCriteria"
          label="Which of the following criteria do they qualify for?"
          defaultValue={
            investmentEntity.investorApplication?.accreditationEligibilityCriteria || []
          }
          compact={true}
          options={[
            {
              value: AccreditationEligibilityCriteriaEnumType.AnnualIncome,
              label: 'Annual Income',
            },
            {
              value: AccreditationEligibilityCriteriaEnumType.NetWorth,
              label: 'Net Worth',
            },
            {
              value: AccreditationEligibilityCriteriaEnumType.Combined,
              label: 'Combined',
            },
            {
              value: AccreditationEligibilityCriteriaEnumType.JointApplicant,
              label: 'Joint Application',
            },
          ]}
        />
      </FormPanelWithReadMode>

      <p className="font-medium mt-8">Reminders Sent</p>
      <DataTable
        data={investmentEntity.investorReminders}
        columns={[
          {
            label: 'Type',
            fieldName: 'type',
          },
          {
            label: 'Sent At',
            fieldName: 'sentAt',
            type: 'DATETIME',
          },
          {
            label: 'Email Status',
            fieldName: 'emailStatus',
          },
        ]}
      />
    </div>
  );
};

export default InvestmentEntityInvestorApplication;
